import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckIcon } from "@heroicons/react/24/outline";
import MarketingLayout from "../components/MarketingLayout";
import SEO from "../components/SEO";
import { apiService } from "../services/api";

const FALLBACK_PLANS = [
  {
    name: "Starter",
    description: "For small clubs getting started with evaluations.",
    price: "Free",
    period: "",
    highlight: false,
    features: [
      "Up to 30 players",
      "1 evaluation session",
      "2 evaluators",
      "Basic skill categories",
      "Player rankings",
      "CSV roster import",
    ],
    cta: "Get Started",
    ctaLink: "/portal/login",
  },
  {
    name: "Basic",
    description: "For clubs that need the full evaluation workflow.",
    price: "$7",
    period: "/per player /per evaluation",
    highlight: true,
    features: [
      "Unlimited players",
      "Unlimited evaluations",
      "Unlimited evaluators",
      "Custom skill categories & weights",
      "Team builder tool",
      "Role-based staff access",
      "Percentile rankings",
      "Priority support",
    ],
    cta: "Contact Us",
    ctaLink: "/portal/login",
  },
  {
    name: "Elite",
    description: "For clubs that want advanced features and support.",
    price: "$10",
    period: "/per player /per evaluation",
    highlight: false,
    features: [
      "Unlimited players",
      "Unlimited evaluations",
      "Unlimited evaluators",
      "Everything in Basic, plus:",
      "Import from TeamSnap & SportsEngine",
      "Custom branding on reports",
      "Parent access to reports",
      "Phone & email support",
    ],
    cta: "Contact Us",
    ctaLink: "/portal/login",
  },
];

const FALLBACK_FAQS = [
  {
    q: "Can I try Eval Ninja before committing?",
    a: "Yes! The Starter plan is completely free and lets you run an evaluation with up to 30 players.",
  },
  {
    q: "How does billing work?",
    a: "Plans are billed monthly. You can upgrade, downgrade, or cancel at any time. No long-term contracts.",
  },
  {
    q: 'What counts as a "player"?',
    a: "Any player in your active roster counts toward your limit. You can archive players from past seasons without affecting your count.",
  },
  {
    q: "Can I import players from my existing platform?",
    a: "Yes! We support direct import from TeamSnap and SportsEngine, and CSV upload works with any platform. The Starter plan supports CSV only.",
  },
  {
    q: "Do parents need an account?",
    a: "No. Parents receive a secure, token-based link via email that lets them view their child's report without creating an account.",
  },
  {
    q: "What sports does Eval Ninja support?",
    a: "Eval Ninja works for any sport. You define your own skill categories, so it adapts to hockey, soccer, basketball, baseball, lacrosse, and more.",
  },
];

function mapApiPlan(plan) {
  return {
    name: plan.name,
    description: plan.description,
    price: plan.price_display || "",
    period: plan.price_period || "",
    highlight: plan.highlight || false,
    features: Array.isArray(plan.features) ? plan.features : [],
    cta: plan.cta_text || "Contact Us",
    ctaLink: plan.cta_link || "/portal/login",
  };
}

function PlanSkeleton() {
  return (
    <div className="rounded-2xl p-8 bg-white border border-gray-200 shadow-sm animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
      <div className="h-4 bg-gray-100 rounded w-2/3 mb-6" />
      <div className="h-10 bg-gray-200 rounded w-1/2 mb-6" />
      <div className="space-y-3 mb-8">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-4 bg-gray-100 rounded" />
        ))}
      </div>
      <div className="h-10 bg-gray-200 rounded" />
    </div>
  );
}

export default function PricingPage() {
  const [plans, setPlans] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);
  const [faqs, setFaqs] = useState(FALLBACK_FAQS);

  useEffect(() => {
    apiService
      .getPublicPlans()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setPlans(data.map(mapApiPlan));
        } else {
          setPlans(FALLBACK_PLANS);
        }
      })
      .catch(() => {
        setLoadError(true);
        setPlans(FALLBACK_PLANS);
      });

    apiService
      .getPublicFaqs()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setFaqs(data.map((f) => ({ q: f.question, a: f.answer })));
        }
      })
      .catch(() => {
        // keep FALLBACK_FAQS on error
      });
  }, []);

  const displayPlans = plans ?? [];
  const loading = plans === null;

  return (
    <MarketingLayout>
      <SEO
        title="Pricing"
        path="/pricing"
        description="Free for small clubs. Paid plans from $7/per player for unlimited evaluations, advanced analytics, and parent reports. No long-term contracts."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a,
              },
            })),
          })}
        </script>
      </SEO>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Try it free and scale as your program grows. No hidden fees, no
            long-term contracts.
          </p>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="pb-20 -mt-4 bg-gradient-to-b from-sky-50/0 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {loading ? (
              <>
                <PlanSkeleton />
                <PlanSkeleton />
                <PlanSkeleton />
              </>
            ) : (
              displayPlans.map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-2xl p-8 flex flex-col ${
                    plan.highlight
                      ? "bg-slate-800 text-white ring-4 ring-sky-500/20 shadow-xl relative"
                      : "bg-white border border-gray-200 shadow-sm"
                  }`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-sky-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="mb-6">
                    <h3
                      className={`text-xl font-bold ${
                        plan.highlight ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {plan.name}
                    </h3>
                    <p
                      className={`mt-2 text-sm ${
                        plan.highlight ? "text-slate-300" : "text-gray-500"
                      }`}
                    >
                      {plan.description}
                    </p>
                  </div>
                  <div className="mb-6">
                    <span
                      className={`text-4xl font-bold ${
                        plan.highlight ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span
                        className={`text-base ${
                          plan.highlight ? "text-slate-300" : "text-gray-500"
                        }`}
                      >
                        {plan.period}
                      </span>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckIcon
                          className={`w-5 h-5 mt-0.5 shrink-0 ${
                            plan.highlight ? "text-sky-400" : "text-sky-500"
                          }`}
                        />
                        <span
                          className={`text-sm ${
                            plan.highlight ? "text-slate-200" : "text-gray-600"
                          }`}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={plan.ctaLink}
                    className={`block text-center px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                      plan.highlight
                        ? "bg-sky-500 hover:bg-sky-600 text-white"
                        : "bg-gray-900 hover:bg-gray-800 text-white"
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))
            )}
          </div>
          {loadError && (
            <p className="text-center text-xs text-gray-400 mt-4">
              Showing cached pricing — live data unavailable.
            </p>
          )}
        </div>
      </section>

      {/* Comparison callout */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            All Plans Include
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
            {[
              {
                title: "Mobile-Ready",
                description:
                  "Score players from any device — phone, tablet, or desktop.",
              },
              {
                title: "Real-Time Sync",
                description:
                  "Multiple evaluators score simultaneously with instant updates.",
              },
              {
                title: "Secure & Private",
                description:
                  "Role-based access ensures everyone sees only what they should.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-xl p-6 border border-gray-100"
              >
                <h3 className="font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {faqs.map((faq) => (
              <div key={faq.q}>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.q}
                </h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-slate-700 to-slate-800 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Start Running Better Evaluations Today
          </h2>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-md text-slate-800 bg-white hover:bg-gray-100 transition-colors shadow-sm"
          >
            Contact Us To Get Started
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
